import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ZardInputDirective } from 'src/app/@core/components/ui/zard/input/input.directive';
import { ZardInputVariants } from 'src/app/@core/components/ui/zard/input/input.variants';

@Component({
    selector: 'app-input',
    imports: [
        CommonModule,
        ZardInputDirective
    ],
    standalone: true,
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        }
    ]
})
export class InputComponent implements ControlValueAccessor, OnInit {
    @Input() type: string = 'text';
    @Input() placeholder: string = '';
    @Input() disabled: boolean = false;
    @Input() readonly: boolean = false;
    @Input() required: boolean = false;
    @Input() maxlength?: number;
    @Input() minlength?: number;
    @Input() min?: number; // For number inputs
    @Input() max?: number; // For number inputs
    @Input() id?: string;
    @Input() class?: string;
    @Input() rows?: number;
    @Input() cols?: number;

    // Zard UI Input properties
    @Input() size: ZardInputVariants['zSize'] = 'default';
    @Input() status?: ZardInputVariants['zStatus'];
    @Input() borderless: boolean = false;

    @Output() valueChange = new EventEmitter<string>();
    @Output() blur = new EventEmitter<FocusEvent>();
    @Output() focus = new EventEmitter<FocusEvent>();
    @Output() input = new EventEmitter<Event>();

    @ViewChild('inputElement', { static: false }) inputElement?: ElementRef<HTMLInputElement | HTMLTextAreaElement>;

    value: string = '';
    isTextarea: boolean = false;

    private onChange = (value: string) => { };
    private onTouched = () => { };

    ngOnInit() {
        // Determine if this should be a textarea based on rows or cols input
        this.isTextarea = this.type === 'textarea' || this.rows !== undefined || this.cols !== undefined;
    }

    writeValue(value: string): void {
        this.value = value || '';
        // Update the DOM element directly for ControlValueAccessor
        if (this.inputElement?.nativeElement) {
            this.inputElement.nativeElement.value = this.value;
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onInput(event: Event): void {
        const target = event.target as HTMLInputElement | HTMLTextAreaElement;
        let newValue = target.value;

        if (this.type === 'number' && newValue !== '') {
            const num = parseFloat(newValue);
            if (this.min !== undefined && num < this.min) {
                newValue = String(this.min);
                (target as HTMLInputElement).value = newValue;
            } else if (this.max !== undefined && num > this.max) {
                newValue = String(this.max);
                (target as HTMLInputElement).value = newValue;
            }
        }

        this.value = newValue;
        this.onChange(newValue);
        this.valueChange.emit(newValue);
        this.input.emit(event);
    }

    onBlur(event: FocusEvent): void {
        this.onTouched();
        this.blur.emit(event);
    }

    onFocus(event: FocusEvent): void {
        this.focus.emit(event);
    }

    focusInput(): void {
        const el = this.inputElement?.nativeElement as HTMLInputElement | undefined;
        el?.focus();
        el?.select();
    }
}

